import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, OnInit, signal, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { L10N_LOCALE, L10nLocale } from 'angular-l10n';
import { catchError, of, tap } from 'rxjs';
import { AlertRecipientsHelper } from 'src/app/alert/shared/alert-builder';
import { AccessFilterCondition } from 'src/app/handweave-api';
import { DistributionListsApi } from 'src/app/hickory-api';
import { AccessRecipe, AlertSettings, DistributionProfileSummary } from 'src/app/hickory-api/model/models';
import { TenantApi } from 'src/app/platform-directory-api';
import { ApiConfig } from 'src/app/shared/api-config/api-config';
import { AuthService } from 'src/app/shared/authentication/auth.service';
import { DirectoryGroup, DirectoryUser } from 'src/app/shared/directory/base/models';
import { CombinedDirectoryService } from 'src/app/shared/directory/combined/combined-directory.service';
import { LogHelper } from 'src/app/shared/logging/log-helper';
import { AccessRecipeHelper } from 'src/app/shared/model/accessrecipe-helper';
import { calcPageCount, toInteger } from 'src/app/shared/model/utility';
import { SimpleTableDialogComponent } from 'src/app/shared/simple-table-dialog/simple-table-dialog.component';
import { ContactDetailsDialogComponent } from '../contact-details-dialog/contact-details-dialog.component';
import { GroupDetailsDialogComponent } from '../group-details-dialog/group-details-dialog.component';


@Component({
  selector: 'app-recipient-settings',
  templateUrl: './recipient-settings.component.html',
  styleUrls: ['./recipient-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false
})
export class RecipientSettingsComponent implements OnChanges, OnInit {
  readonly locale = inject<L10nLocale>(L10N_LOCALE);
  private readonly authService = inject(AuthService);
  private readonly directory = inject(CombinedDirectoryService);
  private readonly distributionListsApi = inject(DistributionListsApi);
  private readonly tenantApi = inject(TenantApi);
  private readonly dialog = inject(MatDialog);

  
  @Input() isScheduled: boolean = false;
  @Input() alert: AlertSettings;

  distributionList: DistributionProfileSummary;
  distributionListLoading: boolean;

  groupsCount: number;
  groupsResolved: DirectoryGroup[];
  groupsLoading: boolean;

  usersCount: number;
  usersResolved = signal<DirectoryUser[]>([]); // fix issue where sometimes users don't update correctly
  usersLoading: boolean;
  usersHasMore: boolean;
  usersPage: number = 0;
  usersPageSize: number = 25;

  geoFencesCount: number;
  citiesCount: number;
  searchesCount: number;

  searchFields: Record<string, string> = {};

  get searches(): AccessRecipe[] { return this.alert?.recipients?.searchFilters?.filter((f) => AccessRecipeHelper.isValid(f)) ?? []; }
  get hasViewContacts(): boolean { return this.authService.isAdminUser(); }
  // ROAD-9840 - we need a way to tell what kind of sources are present
  get hasDistSources(): boolean { return !!this.distributionList?.sources?.some(_ => true); }

  ngOnInit(): void {
    this.fetchSearchFields();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.alert?.currentValue) {
      this.groupsCount = AlertRecipientsHelper.combinedGroupCount(null, null, this.alert?.recipients);
      this.usersCount = AlertRecipientsHelper.combinedContactCount(null, null, this.alert?.recipients);
      this.searchesCount = AlertRecipientsHelper.validSearchCount(null, null, this.alert?.recipients);
      this.geoFencesCount = this.alert?.recipients?.geofences?.features?.length ?? 0;
      this.citiesCount = this.alert?.recipients?.municipals?.cities?.length ?? 0;
      this.usersResolved.set([]); // fix duplicate users when alert changes
      this.resolveGroups();
      this.resolveUsers();
      this.resolveDistributionList();
    }
  }

  resolveUsers() {
    if (!this.usersCount) return
    this.usersPage += 1;
    const pageCount = calcPageCount(this.usersCount, this.usersPageSize);

    this.usersLoading = true;
    this.directory.resolveUsers(this.alert?.recipients, false, this.usersPage, this.usersPageSize).subscribe((res => {
      this.usersResolved.set((this.usersResolved() ?? []).concat(res?.items ?? []).sort((a, b) => a.lastFirstName?.localeCompare(b.lastFirstName) ?? 0));
      this.usersLoading = false;
      this.usersHasMore = pageCount > this.usersPage;
    }));
  }

  resolveGroups() {
    if (!this.groupsCount) return
    this.groupsLoading = true;
    this.directory.resolveGroups(this.alert?.recipients, 1, this.groupsCount).subscribe((res => {
      this.groupsResolved = res?.items;
      this.groupsLoading = false;
    }));
  }

  resolveDistributionList() {
    const id = this.alert?.recipients?.distributionListId;
    const tenantId = toInteger(ApiConfig.tenantId);
    if (!id || !tenantId) return;
    if (this.distributionList?.id === id) return;
    this.distributionListLoading = true;
    this.distributionListsApi.getDistributionList(tenantId, id).subscribe({
      next: res => {
        this.distributionList = res?.item;
        this.distributionListLoading = false;
      }, error: _err => {
        this.distributionListLoading = false;
      }
    });
  }

  private fetchSearchFields() {
    this.tenantApi.tenantGetFieldConfigurationDisplayNames(ApiConfig.tenantId, 'usersv2')
    .pipe(
      tap((res) => {
        this.searchFields = res;
      }),
      catchError((err) => {
        LogHelper.debug('AlertDetailsRecipientsTabComponent', 'Error fetching search fields', err);
        return of(undefined);
      })
    )
    .subscribe();
  }

  onGroupClick(item: DirectoryGroup) {
    this.dialog.open(GroupDetailsDialogComponent, {
      minWidth: '600px',
      data: { groupId: item.hickoryId, summary: item.toHkGroupSummary(), title: 'Group Details' },
    });
  }

  onUserClick(item: DirectoryUser) {
    this.dialog.open(ContactDetailsDialogComponent, {
      minWidth: '600px',
      data: { contactId: item.hickoryId, title: 'Recipient Details' },
    });
  }

  onFilterClick(filter: AccessFilterCondition, displayName?: string) {
    const datasource = new MatTableDataSource<{value: string}>(filter.values?.map(v => ({value: v})) ?? []);

    this.dialog.open(SimpleTableDialogComponent, {
      minWidth: '600px',
      height: '800px',
      data: { title: displayName ?? filter.field, datasource, columns: [{ key: 'value', label: 'Value' }] },
    });
  }

  onUsersLoadMore() {
    this.resolveUsers();
  }

  closeDialogs() {
    this.dialog.closeAll();
  }
}
